import { useMutation, useQuery } from '@apollo/client';
import {
  COMMON_CONSTANTS,
  SuperTable,
  getDefaultValue,
  getParsedCrudData,
  getTranslatedText,
  toast,
  useIntl,
  useIsAuthorisedHook,
  useProduct,
  withErrorBoundary
} from '@nile/components';
import {
  GET_TICKET_ASSETS,
  RMMClient,
  UPDATE_TICKET_ASSETS,
  GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS,
  AccountClient
} from '@nile/graphql';
import {
  fetchValues,
  getAsyncOptions
} from '@nile/rmm/src/modules/asset-management/components/sub-pages/all-monitored-assets-view/all-monitored-assets-view-filter-helper.ts';
import envConfig from '@nile/home/src/Config/env';
import { ALL_ASSET_TYPES } from '@nile/rmm/src/constants';
import { GET_ASSETS_FILTER } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import {
  Button,
  ButtonGroup,
  Heading,
  Sheet
} from '@superops/ui-design-system';
import { isEmpty, pick } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

function TicketAssetsSheet(props) {
  const { ticket, open, newTicket } = props;
  const [ticketAssets, setTicketAssets] = useState(props?.ticketAssets || []);
  const intl = useIntl();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const isAssetAuthorised = useIsAuthorisedHook('asset.view');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [updating, setUpdating] = useState(false);

  const fetchAssetList = (assetIds) => {
    setLoading(true);
    RMMClient.query({
      query: GET_ASSETS_FILTER,
      fetchPolicy: 'network-only',
      variables: {
        viewType: ALL_ASSET_TYPES.ALL_MONITORED_ASSET,
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'assetId',
                operator: 'includes',
                value: assetIds || []
              }
            ]
          }
        }
      }
    })
      .then((res) => {
        const selectedAssetsInTicket =
          ticketAssets || res?.data?.getAllAssets?.assets || [];
        setSelectedAssets(selectedAssetsInTicket);
        setTicketAssets(selectedAssetsInTicket);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const selectedAssetsInTicket = ticketAssets || [];
    setSelectedAssets(selectedAssetsInTicket);
    setTicketAssets(selectedAssetsInTicket);
  }, [ticketAssets]);

  const {
    data: ticketAssetsData,
    loading: ticketAssetsLoading,
    error: ticketAssetsError,
    refetch: assetRefetch
  } = useQuery(GET_TICKET_ASSETS, {
    skip: isEmpty(ticket?.assets),
    variables: {
      id: ticket.ticketId
    },
    onCompleted(res) {
      if (res?.getTicket) {
        if (isAssetAuthorised) {
          fetchAssetList(
            res.getTicket.assets.map((asset) => {
              return asset.assetId;
            })
          );
        } else {
          const selectedAssetsInTicket = res?.getTicket.assets || [];
          setSelectedAssets(selectedAssetsInTicket);
          setTicketAssets(selectedAssetsInTicket);
        }
      }
    }
  });

  const [associateAssetsWithTicket] = useMutation(UPDATE_TICKET_ASSETS);
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);
  function getDefaulOperands() {
    if (isMSPProduct) {
      return [
        {
          attribute: 'client.accountId',
          operator: 'is',
          value: ticket?.client?.accountId
        }
      ];
    }
    return [
      {
        attribute: 'site.id',
        operator: 'is',
        value: ticket?.site?.id
      }
    ];
  }
  const DEFAULT_OPERANDS = getDefaulOperands();

  const [conditions, setConditions] = useState(null);
  const conditionRef = React.useRef(null);
  conditionRef.current = conditions;
  const ROW_COUNT = 20;

  const updateConditions = (condition) => {
    setConditions(condition);
  };

  const getOperands = (updatedCondition) => {
    let defaultOperands = DEFAULT_OPERANDS;
    if (updatedCondition?.operands) {
      defaultOperands = [...defaultOperands, ...updatedCondition?.operands];
    }
    return defaultOperands;
  };

  const resetFilter = () => {
    setConditions(null);
  };

  const context = {
    records: selectedAssets,
    setRecords: setSelectedAssets,
    viewConditions: conditions
  };

  // Helpers for asset filter

  const checkDynamicQuery = (col) => {
    switch (col.name) {
      case 'name':
        return true;
      case 'owner.name':
        return true;
      default:
        return false;
    }
  };

  const getDynamicQuery = (col) => {
    let client;
    let query;
    switch (col.name) {
      case 'name':
        client = RMMClient;
        query = GET_ASSETS_FILTER;
        return client.query({
          query: query,
          variables: {
            viewType: ALL_ASSET_TYPES.ALL_MONITORED_ASSET,
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              },
              condition: {
                joinOperator: 'AND',
                operands: DEFAULT_OPERANDS
              }
            }
          }
        });
      case 'owner.name':
        client = AccountClient;
        query = GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS;
        return client.query({
          query: query,
          variables: {
            input: {
              listInfo: {
                condition: {
                  joinOperator: 'AND',
                  operands: DEFAULT_OPERANDS
                }
              }
            }
          }
        });
      default:
        return null;
    }
  };

  const generateOptions = (col, data) => {
    let allValues = [];
    switch (col.name) {
      case 'name':
        allValues = data?.getAllAssets.assets.map((el) => ({
          name: el.name,
          value: el.name,
          label: el.name
        }));
        return allValues;
      case 'owner.name':
        allValues = data?.getClientUserAssociationsList.associations.map(
          (el) => ({
            name: el.user.name,
            value: el.user.name,
            label: el.user.name
          })
        );
        return allValues;
      default:
        return allValues;
    }
  };

  // End helpers for asset Filter

  const fetchAssets = (personalization, pageOptions) => {
    setLoading(true);
    setError(false);
    const operands = getOperands(conditionRef.current);
    RMMClient.query({
      query: GET_ASSETS_FILTER,
      fetchPolicy: 'network-only',
      variables: {
        viewType: ALL_ASSET_TYPES.ALL_MONITORED_ASSET,
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: operands
          },
          page: pageOptions?.page || 1,
          pageSize: ROW_COUNT,
          sort: pageOptions?.sort || [
            {
              attribute: 'name',
              order: 'ASC'
            }
          ]
        }
      }
    })
      .then((res) => {
        setData(res?.data?.getAllAssets?.assets);
        const cols = [
          {
            name: 'name',
            label: getTranslatedText(
              intl,
              'ticket.details.insights.assets.assetName.chkbox'
            ),
            filterType: 'select',
            dataType: 'string',
            disableFilters: false
          },
          {
            name: 'owner.name',
            label: getTranslatedText(intl, 'entity.requester'),
            filterType: 'select',
            dataType: 'string',
            disableFilters: false,
            autoComplete: false
          }
        ];
        const columns = cols.map((col, idx) => {
          return {
            Header: col.label,
            header: col.label,
            accessor: col.name,
            accessorKey: col.name,
            type: 'Text',
            id: col.name,
            disableResizing: false,
            field: col,
            disableFilters: col?.disableFilters,
            autoComplete: !!col?.autoComplete,
            helpers: {
              getDefaultValue,
              checkDynamicQuery,
              updateConditions,
              getDynamicQuery,
              generateOptions,
              getAsyncOptions,
              fetchValues,
              getDynamicQuery
            }
          };
          // columnGroup[0].columns.push(column);
        });
        setData(res?.data?.getAllAssets?.assets);
        setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
        setListInfo(res?.data?.getAllAssets?.listInfo);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setError(true);
      });
  };
  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [conditions]);

  const associateAssets = (asset) => {
    const currentAssociatedAssets = ticketAssets || [];
    const newAssociatedAssets = selectedAssets;
    const parsedObject = getParsedCrudData(
      currentAssociatedAssets,
      newAssociatedAssets,
      'assetId'
    );

    let variables = {
      id: ticket.ticketId
    };
    if (parsedObject?.addedItems && parsedObject?.addedItems?.length) {
      const addAssets = parsedObject.addedItems.map((el) =>
        pick(el, ['assetId', 'name', 'assetClass.classId', 'assetClass.name'])
      );
      variables = {
        ...variables,
        addAssets
      };
    }

    if (parsedObject?.deletedItems && parsedObject?.deletedItems?.length) {
      const deleteAssets = parsedObject?.deletedItems.map((el) =>
        pick(el, ['assetClass.classId', 'assetClass.name', 'assetId', 'client'])
      );
      variables = {
        ...variables,
        deleteAssets
      };
    }

    setUpdating(true);
    if (!newTicket) {
      associateAssetsWithTicket({
        variables: variables,
        input: variables
      })
        .then((res) => {
          setUpdating(false);
          if (res?.data?.updateTicket) {
            toast('success', 'ticket.details.assets.assetAssociate.success');
          }
          props.toggleSheetActive();
          props?.refetchAssets();
        })
        .catch((err) => {
          setUpdating(false);
          // toast('error', 'Something went wrong');
        });
    } else {
      setUpdating(false);
      props.toggleSheetActive();
      props?.addedAssets(variables || []);
    }
  };

  if (error) return <div className="vw30">error...</div>;

  return (
    <Sheet open={open} onClose={props.toggleSheetActive}>
      <div className="vw40 bg-white full-height p20">
        <div className="flex actr">
          <Heading element="h2" weight="regular">
            <FormattedMessage id="ticket.details.insights.assets.allAssets.header" />
          </Heading>
          <div className="mlauto">
            <ButtonGroup>
              <Button onClick={props.toggleSheetActive} plainSecondary>
                <FormattedMessage id="common.cancel" />
              </Button>

              <Button loading={updating} onClick={associateAssets} primary>
                <FormattedMessage id="common.save" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className="asset-table-container mt40 ch-60">
          {conditionRef.current && (
            <div className="flex jend mb10">
              <Button plain onClick={resetFilter}>
                <FormattedMessage id="ticket.details.resetFilters" />
              </Button>
            </div>
          )}
          <SuperTable
            columns={columnGroup}
            data={data}
            setData={setData}
            rowCount={ROW_COUNT}
            refreshTable={fetchAssets}
            skipPageReset
            listInfo={listInfo}
            context={context}
            isLoading={loading || updating || ticketAssetsLoading}
            maintainSelection
            idKey="assetId"
            pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
            pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
            activePage={listInfo.page || 0}
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/asset-emplty-stat.svg`,
              heading: getTranslatedText(
                intl,
                'ticket.insights.assets.noAssetsFound.label'
              )
            }}
          />
        </div>
      </div>
    </Sheet>
  );
}

export default withErrorBoundary(TicketAssetsSheet);
